import type { AppConfig, ApiConfig, AuthConfig } from './environment';

// Define the structure of the runtime config file
interface RuntimeConfig {
  env: 'development' | 'staging' | 'production';
  region: string;
  cognito_user_pool_id: string;
  cognito_user_pool_client_id: string;
  cognito_domain: string;
  api_base_url: string;
  api_admin_base_url: string;
}

class ConfigLoaderError extends Error {
  constructor(message: string) {
    super(`Configuration Loader Error: ${message}`);
    this.name = 'ConfigLoaderError';
  }
}

async function fetchRuntimeConfig(): Promise<Partial<RuntimeConfig>> {
  // In development, runtime-config.json may not exist, which is fine.
  // In deployed environments, it SHOULD exist.
  try {
    const response = await fetch('/runtime-config.json');
    if (response.ok) {
      return await response.json();
    }
    // For non-OK responses (like 404), treat as if the file doesn't exist.
    return {};
  } catch (error) {
    // Network errors, etc.
    console.warn('Could not fetch runtime-config.json. Falling back to environment variables.', error);
    return {};
  }
}

function getLocalOverrides(): Partial<RuntimeConfig> {
  const overrides: Partial<RuntimeConfig> = {};
  if (import.meta.env.VITE_COGNITO_USER_POOL_ID) {
    overrides.cognito_user_pool_id = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  }
  if (import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID) {
    overrides.cognito_user_pool_client_id = import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID;
  }
  if (import.meta.env.VITE_AWS_REGION) {
    overrides.region = import.meta.env.VITE_AWS_REGION;
  }
  if (import.meta.env.VITE_COGNITO_DOMAIN) {
    overrides.cognito_domain = import.meta.env.VITE_COGNITO_DOMAIN;
  }
  if (import.meta.env.VITE_API_BASE_URL) {
    overrides.api_base_url = import.meta.env.VITE_API_BASE_URL;
  }
  if (import.meta.env.VITE_API_ADMIN_BASE_URL) {
    overrides.api_admin_base_url = import.meta.env.VITE_API_ADMIN_BASE_URL;
  }
  return overrides;
}

export async function loadAndValidateConfig(): Promise<AppConfig> {
  const runtimeConfig = await fetchRuntimeConfig();
  const localOverrides = getLocalOverrides();

  // Merge configs: local overrides take precedence over runtime config
  const mergedConfig = { ...runtimeConfig, ...localOverrides };

  const environment = mergedConfig.env || 'development';

  // Validate required fields before constructing the final config
  if (!mergedConfig.cognito_user_pool_id || !mergedConfig.cognito_user_pool_client_id) {
    throw new ConfigLoaderError('Missing required Cognito configuration: User Pool ID and Client ID are required.');
  }

  const auth: AuthConfig = {
    userPoolId: mergedConfig.cognito_user_pool_id,
    userPoolClientId: mergedConfig.cognito_user_pool_client_id,
    region: mergedConfig.region || 'us-east-1',
    domain: mergedConfig.cognito_domain,
  };

  const api: ApiConfig = {
    baseUrl: mergedConfig.api_base_url || (environment === 'development' ? 'http://localhost:3000/api/v1' : ''),
    adminBaseUrl: mergedConfig.api_admin_base_url || (environment === 'development' ? 'http://localhost:3000/admin/v1' : ''),
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10),
  };

  if (!api.baseUrl || !api.adminBaseUrl) {
    throw new ConfigLoaderError('Missing required API configuration: Base URL and Admin Base URL are required.');
  }

  return {
    auth,
    api,
    environment,
    isDevelopment: environment === 'development',
    isStaging: environment === 'staging',
    isProduction: environment === 'production',
  };
}